import {
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import {
  announcementsApi,
  pushMessagesApi,
  type PushMessageCreateInput,
  type PushSendMode,
  type PushTargetApp,
} from '@/services/api';
import {
  PageHeader,
  Section,
  TextInput,
  TextArea,
  Select,
} from '@/components/common';

type LinkMode = 'none' | 'announcement' | 'custom';

interface FormState {
  targetApp: PushTargetApp;
  title: string;
  body: string;
  /** 딥링크 모드 */
  linkMode: LinkMode;
  /** linkMode='announcement'일 때 선택된 공지사항 id */
  linkAnnouncementId: string; // select value는 string
  /** linkMode='custom'일 때 직접 입력된 URL */
  linkUrl: string;
  sendMode: PushSendMode;
  /** 예약 발송 날짜 (YYYY-MM-DD) */
  scheduledDate: string;
  /** 예약 시 (00–23) */
  scheduledHour: string;
  /** 예약 분 (00–59) */
  scheduledMinute: string;
}

const TARGET_OPTIONS: { value: PushTargetApp; label: string }[] = [
  { value: 'BUSINESS', label: '비즈니스앱' },
  { value: 'GUARDIAN', label: '보호자앱' },
];

/** 대상 앱에 따른 공지사항 상세 라우트 */
function buildAnnouncementDeepLink(
  targetApp: PushTargetApp,
  announcementId: number,
): string {
  return targetApp === 'BUSINESS'
    ? `/business/layout/user-owner-announcement-detail?id=${announcementId}`
    : `/customer/layout/user-announcement-detail?id=${announcementId}`;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0'),
);

/** 예약 발송 디폴트: 1시간 뒤 정각 */
function defaultScheduled() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    hour: pad(d.getHours()),
    minute: '00',
  };
}

const TITLE_MAX = 60;
const BODY_MAX = 200;

export function PushMessageCreatePage() {
  const navigate = useNavigate();

  const initialScheduled = defaultScheduled();
  const [form, setForm] = useState<FormState>({
    targetApp: 'GUARDIAN',
    title: '',
    body: '',
    linkMode: 'none',
    linkAnnouncementId: '',
    linkUrl: '',
    sendMode: 'NOW',
    scheduledDate: initialScheduled.date,
    scheduledHour: initialScheduled.hour,
    scheduledMinute: initialScheduled.minute,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** 대상 앱에 맞춰 공지사항 목록 조회 (linkMode='announcement'일 때만) */
  const { data: announcementsData, isLoading: isLoadingAnnouncements } = useQuery({
    queryKey: ['push-create-announcements', form.targetApp],
    queryFn: () =>
      announcementsApi.getAnnouncements({
        targetApp: form.targetApp,
        size: 100,
      }),
    enabled: form.linkMode === 'announcement',
    staleTime: 30 * 1000,
  });

  const announcementOptions = useMemo(
    () => announcementsData?.content ?? [],
    [announcementsData],
  );

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof FormState];
        return next;
      });
    }
  };

  const handleTargetChange = (value: PushTargetApp) => {
    setForm(prev => ({
      ...prev,
      targetApp: value,
      // 대상 앱이 바뀌면 선택된 공지사항은 다른 앱 거라서 초기화
      linkAnnouncementId: '',
    }));
  };

  const handleLinkModeChange = (mode: LinkMode) => {
    setForm(prev => ({
      ...prev,
      linkMode: mode,
      // 다른 모드로 바꿀 때 잔존 값 비움 (UX)
      linkAnnouncementId: mode === 'announcement' ? prev.linkAnnouncementId : '',
      linkUrl: mode === 'custom' ? prev.linkUrl : '',
    }));
    setErrors(prev => {
      const next = { ...prev };
      delete next.linkAnnouncementId;
      delete next.linkUrl;
      return next;
    });
  };

  const buildScheduledAt = (): string | undefined => {
    if (form.sendMode !== 'SCHEDULED') return undefined;
    if (!form.scheduledDate) return undefined;
    const hh = (form.scheduledHour || '00').padStart(2, '0');
    const mm = (form.scheduledMinute || '00').padStart(2, '0');
    return `${form.scheduledDate} ${hh}:${mm}:00`;
  };

  /** linkMode/입력값을 합쳐 최종 link 문자열 생성 */
  const buildLink = (): string | undefined => {
    if (form.linkMode === 'announcement') {
      const id = Number(form.linkAnnouncementId);
      if (!id) return undefined;
      return buildAnnouncementDeepLink(form.targetApp, id);
    }
    if (form.linkMode === 'custom') {
      const trimmed = form.linkUrl.trim();
      return trimmed || undefined;
    }
    return undefined;
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!form.title.trim()) next.title = '제목을 입력해주세요.';
    if (form.title.length > TITLE_MAX) next.title = `제목은 ${TITLE_MAX}자 이하로 입력해주세요.`;
    if (!form.body.trim()) next.body = '본문을 입력해주세요.';
    if (form.body.length > BODY_MAX) next.body = `본문은 ${BODY_MAX}자 이하로 입력해주세요.`;

    if (form.linkMode === 'announcement' && !form.linkAnnouncementId) {
      next.linkAnnouncementId = '공지사항을 선택해주세요.';
    }
    if (form.linkMode === 'custom') {
      const trimmed = form.linkUrl.trim();
      if (!trimmed) {
        next.linkUrl = '딥링크 URL을 입력해주세요.';
      } else if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('/')) {
        next.linkUrl = '딥링크는 http(s):// 로 시작하거나 / 로 시작하는 앱 내부 경로여야 합니다.';
      }
    }

    if (form.sendMode === 'SCHEDULED') {
      const at = buildScheduledAt();
      if (!at) {
        next.scheduledDate = '예약 일시를 입력해주세요.';
      } else {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const nowStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        if (at <= nowStr) {
          next.scheduledDate = '예약 일시는 현재보다 이후여야 합니다.';
        }
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const targetLabel =
        TARGET_OPTIONS.find(o => o.value === form.targetApp)?.label ?? '';
      const payload: PushMessageCreateInput = {
        targetApp: form.targetApp,
        title: form.title.trim(),
        body: form.body.trim(),
        link: buildLink(),
        sendMode: form.sendMode,
        scheduledAt: buildScheduledAt(),
      };
      await pushMessagesApi.createPushMessage(payload);
      if (form.sendMode === 'SCHEDULED') {
        alert(
          `[${targetLabel}] Push 발송이 예약되었습니다.\n예약 시각: ${payload.scheduledAt}`,
        );
      } else {
        alert(
          `[${targetLabel}] Push 발송이 요청되었습니다.\n실제 발송 완료까지 잠시 시간이 걸릴 수 있으며, 결과는 목록에서 확인할 수 있습니다.`,
        );
      }
      navigate('/push-messages');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Push 발송 실패');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="새 Push 발송"
        subtitle="대상 앱을 선택하고 즉시 또는 예약 시점에 Push를 발송합니다."
        right={
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft size={16} />
            뒤로가기
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <Section title="발송 정보">
          <div className="space-y-4">
            <Field label="대상 앱" required>
              <div className="flex flex-wrap gap-3">
                {TARGET_OPTIONS.map(opt => {
                  const checked = form.targetApp === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                        checked
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <input
                        type="radio"
                        name="targetApp"
                        value={opt.value}
                        checked={checked}
                        onChange={() => handleTargetChange(opt.value)}
                        disabled={isSubmitting}
                        className="accent-primary-500"
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field
              label="제목"
              required
              error={errors.title}
              help={`Push 알림의 제목입니다. (${form.title.length}/${TITLE_MAX})`}
            >
              <TextInput
                name="title"
                value={form.title}
                onChange={handleTextChange}
                placeholder="Push 제목"
                maxLength={TITLE_MAX}
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="본문"
              required
              error={errors.body}
              help={`Push 알림의 본문입니다. (${form.body.length}/${BODY_MAX})`}
            >
              <TextArea
                name="body"
                value={form.body}
                onChange={handleTextChange}
                rows={4}
                placeholder="Push 본문 (짧고 명확하게 작성)"
                maxLength={BODY_MAX}
                disabled={isSubmitting}
              />
            </Field>

            <Field
              label="딥링크"
              error={errors.linkAnnouncementId ?? errors.linkUrl}
              help={
                form.linkMode === 'announcement'
                  ? '선택한 공지사항 상세 화면으로 이동하는 딥링크가 자동 생성됩니다.'
                  : form.linkMode === 'custom'
                    ? '앱 내부 경로(/로 시작) 또는 외부 URL(https://)을 입력하세요.'
                    : 'Push 클릭 시 별도 이동 없이 앱 홈으로 들어갑니다.'
              }
            >
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 text-sm">
                  {(['none', 'announcement', 'custom'] as const).map(mode => {
                    const checked = form.linkMode === mode;
                    return (
                      <label
                        key={mode}
                        className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 transition-colors ${
                          checked
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        <input
                          type="radio"
                          name="linkMode"
                          value={mode}
                          checked={checked}
                          onChange={() => handleLinkModeChange(mode)}
                          disabled={isSubmitting}
                          className="accent-primary-500"
                        />
                        {mode === 'none'
                          ? '없음'
                          : mode === 'announcement'
                            ? '공지사항으로 이동'
                            : '직접 입력'}
                      </label>
                    );
                  })}
                </div>

                {form.linkMode === 'announcement' && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    {isLoadingAnnouncements ? (
                      <div className="text-xs text-gray-500">
                        공지사항 목록을 불러오는 중...
                      </div>
                    ) : announcementOptions.length === 0 ? (
                      <div className="text-xs text-gray-500">
                        선택한 대상 앱에 등록된 공지사항이 없습니다. 먼저
                        공지사항을 등록해주세요.
                      </div>
                    ) : (
                      <Select
                        name="linkAnnouncementId"
                        value={form.linkAnnouncementId}
                        onChange={handleTextChange}
                        disabled={isSubmitting}
                      >
                        <option value="">공지사항을 선택하세요</option>
                        {announcementOptions.map(a => (
                          <option key={a.id} value={String(a.id)}>
                            {a.isPinned ? '📌 ' : ''}
                            {a.isImportant ? '[중요] ' : ''}
                            {a.title} (#{a.id})
                          </option>
                        ))}
                      </Select>
                    )}
                    {form.linkAnnouncementId && (
                      <div className="mt-2 break-all text-[11px] text-gray-500">
                        생성된 딥링크:{' '}
                        <code className="rounded bg-white px-1 py-0.5 text-gray-700">
                          {buildAnnouncementDeepLink(
                            form.targetApp,
                            Number(form.linkAnnouncementId),
                          )}
                        </code>
                      </div>
                    )}
                  </div>
                )}

                {form.linkMode === 'custom' && (
                  <TextInput
                    name="linkUrl"
                    value={form.linkUrl}
                    onChange={handleTextChange}
                    placeholder="https://... 또는 /business/layout/..."
                    disabled={isSubmitting}
                  />
                )}
              </div>
            </Field>
          </div>
        </Section>

        <Section title="발송 시점">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {(['NOW', 'SCHEDULED'] as const).map(mode => {
                const checked = form.sendMode === mode;
                return (
                  <label
                    key={mode}
                    className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      checked
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="sendMode"
                      value={mode}
                      checked={checked}
                      onChange={() =>
                        setForm(prev => ({ ...prev, sendMode: mode }))
                      }
                      disabled={isSubmitting}
                      className="accent-primary-500"
                    />
                    {mode === 'NOW' ? '즉시 발송' : '예약 발송'}
                  </label>
                );
              })}
            </div>

            {form.sendMode === 'SCHEDULED' && (
              <Field
                label="예약 일시"
                error={errors.scheduledDate}
                help="입력 시각에 Push가 자동 발송됩니다."
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <TextInput
                    type="date"
                    name="scheduledDate"
                    value={form.scheduledDate}
                    onChange={handleTextChange}
                    disabled={isSubmitting}
                    className="sm:w-44"
                  />
                  <div className="flex items-center gap-1">
                    <Select
                      name="scheduledHour"
                      value={form.scheduledHour}
                      onChange={handleTextChange}
                      disabled={isSubmitting}
                      className="w-20"
                    >
                      {HOUR_OPTIONS.map(h => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </Select>
                    <span className="text-sm text-gray-500">시</span>
                    <Select
                      name="scheduledMinute"
                      value={form.scheduledMinute}
                      onChange={handleTextChange}
                      disabled={isSubmitting}
                      className="w-20"
                    >
                      {MINUTE_OPTIONS.map(m => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Select>
                    <span className="text-sm text-gray-500">분</span>
                  </div>
                </div>
              </Field>
            )}
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send size={16} />
            {isSubmitting
              ? form.sendMode === 'SCHEDULED'
                ? '예약 중...'
                : '발송 중...'
              : form.sendMode === 'SCHEDULED'
                ? 'Push 예약'
                : 'Push 즉시 발송'}
          </button>
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  help?: string;
  children: React.ReactNode;
}
function Field({ label, required, error, help, children }: FieldProps) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {!error && help && <p className="mt-1 text-xs text-gray-500">{help}</p>}
    </div>
  );
}
